import type { WritableComputedRef } from 'vue'
import type { AppSelectOption } from '@brighterly/lib-core-types/ui/selects/interfaces'

import { TaskStatus } from '@brighterly/lib-core-types/tutor/enums'

import { useListsStore } from '@shared/model'

import { TutorTaskModal } from './tutor-tasks/enums'
import { useDisciplineTasksStore } from './tasks/store'
import { useTutorTasksExportApi } from '../api/tutor-tasks/export.hook'
import { useDisciplinePrivileges } from './discipline-privileges.hook'
import { useDisciplineFiltersDraft } from './discipline-filters-draft.hook'
import { buildTaskStatusFilterOptions } from './tutor-tasks/tutor-tasks'
import {
  TUTOR_TASKS_SEARCH_MAX_LENGTH,
  fromTutorTasksDateRange,
  getTutorTasksFilterParams,
  isTaskActorType,
  isTaskType,
  toTutorTasksDateRange,
  tutorTasksFiltersToQuery,
} from '../lib/tutor-tasks-query'

interface FiltersDisciplineTasksReturn {
  refSearch: WritableComputedRef<string>
  refTutorIds: WritableComputedRef<number[]>
  refDueDateRange: WritableComputedRef<Date[] | null>
  refStatuses: WritableComputedRef<string[]>
  refTypes: WritableComputedRef<string[]>
  statusOptions: ComputedRef<AppSelectOption<string>[]>
  typeOptions: ComputedRef<AppSelectOption<string>[]>
  searchMaxLength: number
  isLoading: ComputedRef<boolean>
  isExporting: ComputedRef<boolean>
  canManageTutorTasks: ComputedRef<boolean>
  search: () => void
  clearFilters: () => void
  exportTasks: () => Promise<void>
  openCreateTask: () => void
}

export const useFiltersDisciplineTasks = (): FiltersDisciplineTasksReturn => {
  const route = useRoute()
  const store = useDisciplineTasksStore()
  const listsStore = useListsStore()
  const { canManageTutorTasks } = useDisciplinePrivileges()

  const { refDraft, isLoading, patchDraft, search, clearFilters } = useDisciplineFiltersDraft({
    applied: () => store.getFilters,
    // Typed text keeps its spaces while it is being typed; the applied search is trimmed.
    onApply: filters => store.setFilters({ ...filters, search: filters.search?.trim() || null }),
    onReset: () => store.resetFilters(),
    toQuery: tutorTasksFiltersToQuery,
    loading: () => store.isLoading,
  })

  const refSearch = computed<string>({
    get: () => refDraft.value.search ?? '',
    set: (value) => {
      patchDraft({ search: value.slice(0, TUTOR_TASKS_SEARCH_MAX_LENGTH) || null })
    },
  })

  const refTutorIds = computed<number[]>({
    get: () => refDraft.value.tutorIds,
    set: (value) => {
      patchDraft({ tutorIds: value })
    },
  })

  const refDueDateRange = computed<Date[] | null>({
    get: () => toTutorTasksDateRange(refDraft.value.dueDateFrom, refDraft.value.dueDateTo),
    set: (value) => {
      patchDraft(fromTutorTasksDateRange(value))
    },
  })

  /** Statuses and "Canceled by …" actors share one select; the filters keep them apart. */
  const refStatuses = computed<string[]>({
    get: () => [...refDraft.value.statuses, ...refDraft.value.actorTypes],
    set: (value) => {
      patchDraft({
        statuses: value.filter((item): item is TaskStatus => item === TaskStatus.Planned || item === TaskStatus.Completed),
        actorTypes: value.filter(isTaskActorType),
      })
    },
  })

  const refTypes = computed<string[]>({
    get: () => refDraft.value.types,
    set: (value) => {
      patchDraft({ types: value.filter(isTaskType) })
    },
  })

  const statusOptions = computed<AppSelectOption<string>[]>(() =>
    buildTaskStatusFilterOptions(listsStore.getLists?.tutor_task ?? null))

  const typeOptions = computed<AppSelectOption<string>[]>(() =>
    Object.entries(listsStore.getLists?.tutor_task.type ?? {}).map(([value, text]) => ({ value, text })))

  const isExporting = computed<boolean>(() => store.isExporting)

  /** The export follows the applied filters, the ones the list shows. */
  const exportTasks = async (): Promise<void> => {
    if (store.isExporting) return

    await useTutorTasksExportApi({
      params: {
        discipline: route.params.id as string,
        ...getTutorTasksFilterParams(store.getFilters),
      },
      onStart: () => store.setExporting(true),
      onFinish: () => store.setExporting(false),
    })
  }

  const openCreateTask = (): void => {
    store.openModal(TutorTaskModal.Create)
  }

  return {
    refSearch,
    refTutorIds,
    refDueDateRange,
    refStatuses,
    refTypes,
    statusOptions,
    typeOptions,
    searchMaxLength: TUTOR_TASKS_SEARCH_MAX_LENGTH,
    isLoading,
    isExporting,
    canManageTutorTasks,
    search,
    clearFilters,
    exportTasks,
    openCreateTask,
  }
}
